import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import "./tags.css";

export default function Tags() {
  const queryClient = useQueryClient();

  const mytags = useQuery(["mytags"], () => {
    let token = localStorage.getItem("tokenmob");
    return axios.get(`${process.env.REACT_APP_API_ROOT}/mytags`, {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + token,
      },
    });
  });

  if (mytags.isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-8"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (mytags.isError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading tags. Please try again.</p>
        </div>
      </div>
    );
  }

  const tagsData = mytags.data?.data || {};
  const tagCount = Object.keys(tagsData).length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Tags</h1>
          <div className="bg-blue-50 px-3 py-1 rounded-full">
            <span className="text-blue-700 text-sm font-medium">
              {tagCount} {tagCount === 1 ? 'tag' : 'tags'}
            </span>
          </div>
        </div>
        <p className="text-gray-600 leading-relaxed">
          Here's a comprehensive overview of all the tags you're currently using, 
          along with their associated app and user counts.
        </p>
      </div>

      {/* Tags Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {tagCount === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tags found</h3>
            <p className="text-gray-500">Start creating tags to organize your apps and users.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 w-2/5">
                    Tag Name
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 w-1/5">
                    <div className="flex items-center space-x-2">
                      <span>Apps</span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900 w-1/5">
                    <div className="flex items-center space-x-2">
                      <span>Users</span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                  </th>
                  <th className="w-1/5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.keys(tagsData).map((tag, index) => {
                  const tagData = tagsData[tag];
                  return (
                    <tr 
                      key={tag} 
                      className="hover:bg-blue-50 transition-colors duration-150 ease-in-out group"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="font-medium text-gray-900 group-hover:text-blue-700 transition-colors">
                            {tag}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          <span className="bg-green-100 text-green-800 px-2.5 py-1 rounded-full text-sm font-medium">
                            {tagData.appCount}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          <span className="bg-purple-100 text-purple-800 px-2.5 py-1 rounded-full text-sm font-medium">
                            {tagData.userCount}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {/* <button className="text-gray-400 hover:text-blue-600 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button> */}
                          {/* <button className="text-gray-400 hover:text-red-600 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Footer */}
      {tagCount > 0 && (
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-4">
              <span>Total Tags: <strong className="text-gray-900">{tagCount}</strong></span>
              <span>•</span>
                             <span>
                 Total Tagged Apps: <strong className="text-gray-900">
                   {Object.values(tagsData).reduce((sum, tag) => sum + (Number(tag.appCount) || 0), 0)}
                 </strong>
               </span>
               <span>•</span>
               <span>
                 Total Users: <strong className="text-gray-900">
                   {Object.values(tagsData).reduce((sum, tag) => sum + (Number(tag.userCount) || 0), 0)}
                 </strong>
               </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
